import {
    AfterViewInit,
    Component,
    ComponentRef,
    ElementRef,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {
    AbstractControl,
    AbstractControlOptions,
    AsyncValidatorFn,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ReflectionService } from '../shared/utilities/reflection.service';
import { ServiceLocator } from '../shared/utilities/service-locator';
import { AllValidationErrors } from './form.types';
import { QuestionComponent } from './question/question.component';

@Component({
    selector: 'app-generic-form',
    template: ''
})
export abstract class GenericFormGroupComponent implements OnInit, AfterViewInit, OnDestroy {
    private originalValue: string;
    private _title: string;
    private _where: string;
    private _ignoredControls: Array<UntypedFormControl> = [];

    @Output() selfOutputEvent: EventEmitter<GenericFormGroupComponent> = new EventEmitter<GenericFormGroupComponent>();
    @Output() selfWhereDestroyEvent: EventEmitter<string> = new EventEmitter<string>();
    @Output() validationSummaryEvent: EventEmitter<Array<string>> = new EventEmitter<Array<string>>();
    @ViewChildren(QuestionComponent) questionQueryList: QueryList<QuestionComponent>;
    protected subComponentRefs: Array<ComponentRef<GenericFormGroupComponent>>;
    formGroup: UntypedFormGroup;
    listeningForDirty: boolean = false;
    saving: boolean = false;

    readonly required: string = 'required';
    readonly emailValidation: string = 'email';

    @ViewChild('saveButton', { read: ElementRef }) saveButton: ElementRef;
    protected reflectionService: ReflectionService;

    protected constructor(protected renderer2: Renderer2, @Inject(Boolean) protected listenForDirty: boolean = true) {
        this.reflectionService = ServiceLocator.injector.get(ReflectionService);
    }

    ngOnInit(): void {}

    ngAfterViewInit(): void {
        this.startListenForDirty();
        this.selfOutputEvent.emit(this);
    }

    ngOnDestroy() {
        this.selfWhereDestroyEvent.emit(this._where);
    }

    dirtyMessage(): string {
        if (this.listeningForDirty && this.isDirty()) {
            return this._where ? this._where : 'NO_MESSAGE';
        }
        return undefined;
    }

    getFormValue<T>(): T {
        return this.formGroup.getRawValue() as T;
    }

    getWhere(): string {
        return this._where;
    }

    protected createFormGroup(
        controls: { [p: string]: AbstractControl },
        validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
    ) {
        this.formGroup = new UntypedFormGroup(controls, validatorOrOpts, asyncValidator);
    }

    protected ignoreFormControls(controls: Array<UntypedFormControl>) {
        if (!controls || controls.length === 0) {
            return;
        }
        this._ignoredControls = controls;
        this.setOriginalValue(true);
    }

    protected setWhere(where: string) {
        this._where = where;
    }

    private addRemoveDirtyClass(isDirty: boolean) {
        if (this.saveButton && this.renderer2) {
            let button = this.saveButton.nativeElement;
            if (this.saveButton.nativeElement.nodeName === 'APP-BUTTON') {
                button = this.saveButton.nativeElement.querySelector('button');
            }
            if (!button) {
                return;
            }
            if (isDirty) {
                this.renderer2.removeClass(button, 'is-not-dirty');
                this.renderer2.addClass(button, 'is-dirty');
                this.renderer2.addClass(button, 'mat-elevation-z');
                this._title = button.title;
                this.renderer2.setAttribute(button, 'title', 'You have un-saved changes!');
            } else {
                this.renderer2.removeClass(button, 'is-dirty');
                this.renderer2.addClass(button, 'is-not-dirty');
                this.renderer2.removeClass(button, 'mat-elevation-z');
                this.renderer2.setAttribute(button, 'title', this._title);
            }
        }
    }

    private getFormGroupValue() {
        if (this.formGroup && this.listenForDirty) {
            const value = {};
            for (const controlsKey in this.formGroup.controls) {
                const control = this.formGroup.get(controlsKey) as UntypedFormControl;
                if (this._ignoredControls.indexOf(control) >= 0) {
                    continue;
                }
                value[controlsKey] = control.value;
            }
            return JSON.stringify(value);
        }
        return '';
    }

    public setSaving() {
        this.saving = true;
    }

    public setDoneSaving() {
        this.saving = false;
        if (this.listenForDirty) {
            this.setOriginalValue(true);
            this.addRemoveDirtyClass(false);
        }
    }

    private setOriginalValue(force: boolean = false) {
        if (this.listenForDirty && (!this.originalValue || force)) {
            this.originalValue = this.getFormGroupValue();
        }
    }

    private isDirty(): boolean {
        this.setOriginalValue();
        const currentValue = this.getFormGroupValue();
        return currentValue !== this.originalValue && this.formGroup.valid;
    }

    private startListenForDirty(attempts: number = 0) {
        if (!this.listenForDirty || attempts > 2) {
            return;
        }
        if (this.formGroup && !this.listeningForDirty) {
            this.setOriginalValue();
            this.listeningForDirty = true;
            this.formGroup.statusChanges.pipe(debounceTime(300)).subscribe((next) => {
                this.addRemoveDirtyClass(this.isDirty());
            });
        } else if (!this.listeningForDirty) {
            setTimeout(() => {
                this.startListenForDirty(++attempts);
            }, 300);
        }
    }

    protected isControlRequiredAndDirty(control: UntypedFormControl): boolean {
        if (control) {
            return control.dirty && control.hasError(this.required);
        }
        return false;
    }

    protected setControlRequired(control: UntypedFormControl, updateValid: boolean = true): void {
        this.setControlValidator(control, [Validators.required], updateValid);
    }

    protected setControlValidator(
        control: UntypedFormControl,
        validators: Array<ValidatorFn> | ValidatorFn,
        updateValid: boolean = true
    ): void {
        control.setValidators(validators);
        if (updateValid) {
            control.updateValueAndValidity();
        }
    }

    protected setControlsRequired(controls: UntypedFormControl[]): void {
        if (controls?.length) {
            controls.forEach((c) => {
                this.setControlRequired(c);
            });
        }
    }

    protected setControlsValidators(controls: UntypedFormControl[], validators: ValidatorFn[]): void {
        if (controls?.length && validators?.length) {
            controls.forEach((c) => {
                this.setControlValidator(c, validators);
            });
        }
    }

    protected setAllToDisabled(emitEvent: boolean = false): void {
        if (this.formGroup) {
            Object.keys(this.formGroup.controls).forEach((key) => {
                const control = this.formGroup.get(key);
                this.setToEnabledDisabled(control, true, emitEvent);
            });
        }
    }

    protected setTheseToDisabled(
        controls: { [key: string]: AbstractControl },
        emitEvent: boolean = false,
        partOf: UntypedFormGroup = null
    ) {
        if (!controls) {
            return;
        }
        Object.keys(controls).forEach((key) => {
            const group = partOf ? partOf : this.formGroup;
            const control = group.get(key);
            this.setToEnabledDisabled(control, true, emitEvent);
        });
    }

    private setToEnabledDisabled(control: AbstractControl, disable: boolean, emitEvent: boolean = false): void {
        if (!control) {
            return;
        }
        if (disable) {
            control.disable({ emitEvent: emitEvent });
        } else {
            control.enable({ emitEvent: emitEvent });
        }
    }

    protected setTheseToEnabled(
        controls: { [key: string]: AbstractControl },
        emitEvent: boolean = false,
        partOf: UntypedFormGroup = null
    ) {
        if (!controls) {
            return;
        }
        Object.keys(controls).forEach((key) => {
            const group = partOf ? partOf : this.formGroup;
            const control = group.get(key);
            this.setToEnabledDisabled(control, false, emitEvent);
        });
    }

    protected setAllToEnabled(emitEvent: boolean = false): void {
        if (this.formGroup) {
            Object.keys(this.formGroup.controls).forEach((key) => {
                const control = this.formGroup.get(key);
                this.setToEnabledDisabled(control, false, emitEvent);
            });
        }
    }

    protected setRequired(controlName: string): void {
        this.formGroup.get(controlName).setValidators(Validators.required);
        this.formGroup.get(controlName).updateValueAndValidity();
    }

    protected clearControlsValidators(controls: UntypedFormControl[], clearValues: boolean = true): void {
        if (controls?.length) {
            controls.forEach((c) => {
                this.clearValidators(c, clearValues);
            });
            this.setOriginalValue(true);
        }
    }

    protected clearValidators(control: UntypedFormControl, clearValue: boolean = true): void {
        control.clearValidators();
        control.clearAsyncValidators();
        if (clearValue) {
            control.setValue(null);
        }
        control.updateValueAndValidity();
        this.setOriginalValue(true);
    }

    protected clearRequired(controlName: string): void {
        this.formGroup.get(controlName).clearValidators();
        this.formGroup.get(controlName).setValue(null);
        this.formGroup.get(controlName).updateValueAndValidity();
    }

    collectValidationMessages(): Array<string> {
        if (this.questionQueryList && this.questionQueryList.length > 0) {
            const summary: Array<string> = [];
            this.questionQueryList.forEach((question: QuestionComponent) => {
                if (question?.component?.instance?.input?.control?.invalid) {
                    let em = question.component.instance.errorMessage;
                    if (!em) {
                        question.component.instance.checkForErrors();
                        em = question.component.instance.errorMessage;
                    }
                    summary.push(em);
                }
            });
            if (this.subComponentRefs && this.subComponentRefs.length > 0) {
                this.subComponentRefs.forEach((component, i) => {
                    const subSummary = component.instance.collectValidationMessages();
                    if (subSummary && subSummary.length > 0) {
                        subSummary.forEach((s) => {
                            const num = this.reflectionService.toTitleCase(
                                this.reflectionService.stringifyNumber(i + 1)
                            );
                            const s1 = this.reflectionService.isNotNullOrEmpty(component.instance.getWhere())
                                ? `${num} ${component.instance.getWhere()}: ${s}`
                                : `${num}: ${s}`;
                            summary.push(s1);
                        });
                    }
                });
            }
            return summary;
        }
        return [];
    }

    private computeValidationSummary(): void {
        const summary: Array<string> = this.collectValidationMessages();
        this.validationSummaryEvent.emit(summary);
    }

    protected myFormValidationErrors() {
        return this.getFormValidationErrors(this.formGroup);
    }

    private getFormValidationErrors(group: UntypedFormGroup): AllValidationErrors[] {
        let errors: AllValidationErrors[] = [];
        Object.keys(group.controls).forEach((key) => {
            const control = group.controls[key];
            if (control instanceof UntypedFormGroup) {
                errors = errors.concat(this.getFormValidationErrors(control as UntypedFormGroup));
            }
            if (!control.invalid) {
                return;
            }
            const controlErrors: ValidationErrors = control.errors;
            if (controlErrors !== null) {
                Object.keys(controlErrors).forEach((keyError) => {
                    errors.push({
                        controlName: key,
                        errorName: keyError,
                        errorValue: controlErrors[keyError]
                    });
                });
            }
        });
        return errors;
    }

    isFormGroupValid(): boolean {
        this.checkAllValuesAndValidity();
        const errors = this.myFormValidationErrors();
        if (errors && errors.length > 0) {
            console.log(errors);
            this.computeValidationSummary();
        }
        //check that we are not invalid
        //a form group with all disabled controls does not set the valid boolean to true
        //if a control is disabled make sure to use getRawValue() instead of value
        return !this.formGroup.invalid;
    }

    isFormGroupDirty(): boolean {
        return this.formGroup.dirty;
    }

    checkAllValuesAndValidity() {
        this.checkAllValues(this.formGroup);
    }

    private checkAllValues(formGroup: UntypedFormGroup) {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.controls[key];
            if (control instanceof UntypedFormGroup) {
                this.checkAllValues(control);
            } else {
                control.markAsTouched();
                control.updateValueAndValidity();
            }
        });
    }

    protected setValidatorRequiredIfBoolean(control: UntypedFormControl, truth: boolean) {
        this.setValidatorsIfBoolean(control, truth, Validators.required);
    }

    protected setValidatorsIfBoolean(
        control: UntypedFormControl,
        truth: boolean,
        validators: ValidatorFn | ValidatorFn[] | null
    ) {
        if (truth) {
            control.setValidators(validators);
        }
    }

    protected clearAllValuesAndMarkPristine() {
        this.internalClearAllValuesAndMarkPristine(this.formGroup);
    }

    private internalClearAllValuesAndMarkPristine(formGroup: UntypedFormGroup) {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.controls[key];
            if (control instanceof UntypedFormGroup) {
                this.internalClearAllValuesAndMarkPristine(control);
            } else {
                control.setValue(null);
                control.markAsPristine();
                control.markAsUntouched();
            }
        });
    }
}
